/**
 * WMS Validation Utilities
 *
 * Safe parsing and validation functions to prevent NaN, undefined, and invalid data
 * from corrupting the application state or database.
 */

/**
 * Safely parse a float value, returning undefined for invalid inputs
 * @param value - Value to parse (string, number, or undefined)
 * @param options - Optional configuration
 * @returns Parsed number or undefined
 */
export function safeParseFloat(
  value: string | number | undefined | null,
  options: { min?: number; max?: number; allowZero?: boolean } = {}
): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = typeof value === 'string' ? parseFloat(value) : value;

  // Check for NaN and Infinity
  if (isNaN(parsed) || !isFinite(parsed)) {
    return undefined;
  }

  // Check minimum value
  if (options.min !== undefined && parsed < options.min) {
    return undefined;
  }

  // Check maximum value
  if (options.max !== undefined && parsed > options.max) {
    return undefined;
  }

  // Check zero handling
  if (!options.allowZero && parsed === 0) {
    return undefined;
  }

  return parsed;
}

/**
 * Safely parse an integer value, returning undefined for invalid inputs
 * @param value - Value to parse (string, number, or undefined)
 * @param options - Optional configuration
 * @returns Parsed integer or undefined
 */
export function safeParseInt(
  value: string | number | undefined | null,
  options: { min?: number; max?: number; radix?: number } = {}
): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = typeof value === 'string'
    ? parseInt(value, options.radix || 10)
    : Math.floor(value);

  // Check for NaN
  if (isNaN(parsed)) {
    return undefined;
  }

  // Check minimum value
  if (options.min !== undefined && parsed < options.min) {
    return undefined;
  }

  // Check maximum value (including safe integer check)
  const maxValue = options.max !== undefined
    ? Math.min(options.max, Number.MAX_SAFE_INTEGER)
    : Number.MAX_SAFE_INTEGER;

  if (parsed > maxValue) {
    return undefined;
  }

  return parsed;
}

/**
 * Sanitize a string for safe use in queries
 * Removes MongoDB operators and limits length
 * @param value - String to sanitize
 * @param maxLength - Maximum allowed length (default: 255)
 * @returns Sanitized string or undefined
 */
export function sanitizeString(
  value: string | undefined | null,
  maxLength: number = 255
): string | undefined {
  if (!value || typeof value !== 'string') {
    return undefined;
  }

  // Remove MongoDB operators and dangerous characters
  let sanitized = value.replace(/[{}$]/g, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength);
  }

  return sanitized || undefined;
}

/**
 * Validate and format a date string from ISO format
 * @param isoString - ISO date string
 * @returns YYYY-MM-DD formatted string or empty string
 */
export function extractDateFromISO(isoString: string | undefined | null): string {
  if (!isoString) {
    return '';
  }

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      return '';
    }
    // Format as YYYY-MM-DD for input[type="date"]
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
}

/**
 * Safely join an array with a separator
 * @param arr - Array to join
 * @param separator - Separator string (default: ', ')
 * @param fallback - Fallback value if array is empty (default: '-')
 * @returns Joined string or fallback
 */
export function safeJoin(
  arr: (string | number | boolean)[] | undefined | null,
  separator: string = ', ',
  fallback: string = '-'
): string {
  if (!arr || !Array.isArray(arr) || arr.length === 0) {
    return fallback;
  }
  return arr.filter(item => item != null).join(separator);
}

/**
 * Validate that a value is a safe array before mapping
 * @param value - Value to check
 * @returns Safe array (original or empty array)
 */
export function ensureArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Get a display value with fallback
 * @param value - Value to display
 * @param fallback - Fallback string (default: '-')
 * @returns Display string
 */
export function getDisplayValue<T>(
  value: T | null | undefined,
  fallback: string = '-'
): string {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
}

/**
 * Validate a color string is safe for inline styles
 * @param color - Color string to validate
 * @returns True if color is safe to use
 */
export function isSafeColor(color: string | undefined | null): boolean {
  if (!color || typeof color !== 'string') {
    return false;
  }

  // Allow hex colors (#RGB or #RRGGBB)
  if (/^#[0-9A-Fa-f]{3}$/.test(color) || /^#[0-9A-Fa-f]{6}$/.test(color)) {
    return true;
  }

  // Allow rgb/rgba with valid syntax
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color)) {
    return true;
  }

  // Allow specific named colors (whitelist approach)
  const namedColors = [
    'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'brown',
    'gray', 'grey', 'black', 'white', 'cyan', 'magenta', 'lime', 'indigo',
    'violet', 'navy', 'teal', 'maroon', 'olive', 'silver', 'gold',
  ];

  return namedColors.includes(color.toLowerCase());
}

/**
 * Validate quantity input is within safe bounds
 * @param quantity - Quantity to validate
 * @param max - Maximum allowed value
 * @returns Object with validation result and error message
 */
export function validateQuantity(
  quantity: number | string | undefined | null,
  max?: number
): { valid: boolean; value?: number; error?: string } {
  const parsed = safeParseInt(quantity, { min: 0, max });

  if (parsed === undefined) {
    return {
      valid: false,
      error: 'Please enter a valid quantity',
    };
  }

  if (parsed < 0) {
    return {
      valid: false,
      value: parsed,
      error: 'Quantity must be positive',
    };
  }

  if (max !== undefined && parsed > max) {
    return {
      valid: false,
      value: parsed,
      error: `Quantity cannot exceed ${max}`,
    };
  }

  if (parsed > Number.MAX_SAFE_INTEGER) {
    return {
      valid: false,
      value: parsed,
      error: 'Quantity is too large',
    };
  }

  return {
    valid: true,
    value: parsed,
  };
}

/**
 * Safely build a location code from zone and bin codes
 * @param zoneCode - Zone code
 * @param binCode - Bin code
 * @param fallback - Fallback value (default: '-')
 * @returns Location code or fallback
 */
export function buildLocationCode(
  zoneCode: string | undefined | null,
  binCode: string | undefined | null,
  fallback: string = '-'
): string {
  if (!zoneCode || !binCode) {
    return fallback;
  }
  return `${zoneCode}-${binCode}`;
}

/**
 * Format a currency value safely
 * @param value - Numeric value
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted currency string or fallback
 */
export function formatCurrency(
  value: number | string | undefined | null,
  currency: string = 'USD'
): string {
  const parsed = safeParseFloat(value, { min: 0 });

  if (parsed === undefined) {
    return '-';
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(parsed);
  } catch {
    return `$${parsed.toFixed(2)}`;
  }
}

/**
 * Validate serial number format
 * @param serial - Serial number to validate
 * @returns Object with validation result and error message
 */
export function validateSerialNumber(
  serial: string | undefined | null
): { valid: boolean; error?: string } {
  if (!serial || typeof serial !== 'string') {
    return {
      valid: false,
      error: 'Serial number is required',
    };
  }

  const trimmed = serial.trim();

  if (trimmed.length < 4) {
    return {
      valid: false,
      error: 'Serial number must be at least 4 characters',
    };
  }

  if (trimmed.length > 50) {
    return {
      valid: false,
      error: 'Serial number is too long (max 50 characters)',
    };
  }

  // Allow alphanumeric and common separators
  if (!/^[A-Za-z0-9\-_]+$/.test(trimmed)) {
    return {
      valid: false,
      error: 'Serial number can only contain letters, numbers, hyphens, and underscores',
    };
  }

  return { valid: true };
}
